import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import MiniHeader from '../../../components/layout/MiniHeader/index'
import Button from '../../../components/common/Button/index'
import Card from '../../../components/common/Card/index'
import Typography from '../../../components/common/Typography/index'
import Pagination from '../../../components/common/Pagination/Pagination'
import Select from '../../../components/common/Select'
import {
  fetchAllFeedback,
  createFeedback,
  updateFeedback,
  updateFeedbackStatus,
  forwardFeedback,
  replyToFeedback,
  markFeedbackUnread,
} from '../../../state/redux/actions/feedbackActions'
import {
  IoIosArrowDown,
  IoIosMail,
  IoIosAlbums,
  IoIosMailUnread,
  IoIosMailOpen,
  IoMdMore,
  IoIosClose,
  IoIosCheckmark,
} from 'react-icons/io'
import { BsDownload } from 'react-icons/bs'
import DarkTabs from '../../../components/common/Tabs/DarkTabs'

const priorityOptions = [
  { value: 'all', label: 'All Priorities' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

const timeOptions = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom' },
]

const statusOptions = [
  { value: 'PENDING', label: 'Open' },
  {value: 'ON_GOING', label: 'On-Going'},
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'POST_PONED', label: 'Postponed' },
]

const categoryOptions = [
  { value: 'bug', label: 'Bug' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'others', label: 'Others' },
]

const sourceOptions = [
  { value: 'inApp', label: 'In app' },
  { value: 'website', label: 'Website' },
]

const tabs = [
  {
    id: 'all',
    label: (
      <>
        <IoIosAlbums className="inline mr-1" /> All Tickets
      </>
    ),
  },
  {
    id: 'new',
    label: (
      <>
        <IoIosMail className="inline mr-1" /> New
      </>
    ),
  },
  {
    id: 'on_going',
    label: (
      <>
        <IoIosMailUnread className="inline mr-1" /> On-Going
      </>
    ),
  },
  {
    id: 'resolved',
    label: (
      <>
        <IoIosMailOpen className="inline mr-1" /> Resolved
      </>
    ),
  },
]

const Feedback = () => {
  const dispatch = useDispatch()
  const { feedback, loading, error } = useSelector(state => state.feedback)

  const [openTicket, setOpenTicket] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [selectedPriority, setSelectedPriority] = useState('all')
  const [selectedTime, setSelectedTime] = useState('all')
  const [dateFilter, setDateFilter] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState('new')
  const [selectedCategory, setSelectedCategory] = useState('bug')
  const [selectedSource, setSelectedSource] = useState('inApp')
  const [ticketName, setTicketName] = useState('')
  const [ticketOwner, setTicketOwner] = useState('Anselm Raj')
  const [currentPage, setCurrentPage] = useState(1)
  const [activeTab, setActiveTab] = useState('all')
  const [itemsPerPage] = useState(10)
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const [showPropertiesModal, setShowPropertiesModal] = useState(false)
  const [showForwardModal, setShowForwardModal] = useState(false)
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false)
  const [selectedRespondent, setSelectedRespondent] = useState('')
  const [respondentSearch, setRespondentSearch] = useState('')
  const [showStatusSelect, setShowStatusSelect] = useState(false)
  const [showPrioritySelect, setShowPrioritySelect] = useState(false)
  const [showCategorySelect, setShowCategorySelect] = useState(false)
  const [showSourceSelect, setShowSourceSelect] = useState(false)
  const [replySubject, setReplySubject] = useState('')
  const [replyMessage, setReplyMessage] = useState('')

  const ticketNameRef = useRef(null)
  const optionsButtonRef = useRef(null)
  const optionsMenuRef = useRef(null)
  const replyTextareaRef = useRef(null)

  useEffect(() => {
    dispatch(fetchAllFeedback())
  }, [dispatch])

  useEffect(() => {
    // Close menu when clicking outside
    const handleClickOutside = event => {
      if (
        optionsMenuRef.current &&
        !optionsMenuRef.current.contains(event.target) &&
        optionsButtonRef.current &&
        !optionsButtonRef.current.contains(event.target)
      ) {
        setShowOptionsMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Helper function to check if a date is within a time range
  const isWithinTimeRange = (date, timeRange) => {
    const now = new Date()
    const targetDate = new Date(date)

    switch (timeRange) {
      case 'today':
        return targetDate.toDateString() === now.toDateString()
      case 'week':
        const startOfWeek = new Date(now)
        startOfWeek.setDate(now.getDate() - now.getDay())
        startOfWeek.setHours(0, 0, 0, 0)
        return targetDate >= startOfWeek
      case 'month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        return targetDate >= startOfMonth
      case 'all':
        return true
      default:
        return true
    }
  }

  // Filter feedback by date range
  const filterFeedbackByDate = (feedbackList, startDate, endDate) => {
    if (!startDate || !endDate || !feedbackList || !Array.isArray(feedbackList)) {
      return feedbackList || []
    }

    return feedbackList.filter(item => {
      const feedbackDate = new Date(item.createdAt)
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Set time boundaries
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      return feedbackDate >= start && feedbackDate <= end
    })
  }

  // Filter by tab, priority, and time
  const filteredFeedback = Array.isArray(feedback)
    ? feedback.filter(item => {
        // Apply date filter first if actively filtering
        let dateMatch = true
        if (isFiltering && dateFilter) {
          const filteredByDate = filterFeedbackByDate([item], dateFilter.startDate, dateFilter.endDate)
          dateMatch = filteredByDate.length > 0
        } else if (!isFiltering && selectedTime !== 'all') {
          // Use existing time filter when not using MiniHeader filtering
          dateMatch = item.createdAt && isWithinTimeRange(item.createdAt, selectedTime)
        }

        // Tab filter
        let tabMatch = true
        if (activeTab === 'new') tabMatch = item.status === 'PENDING'
        else if (activeTab === 'on_going') tabMatch = item.status === 'ON_GOING'
        else if (activeTab === 'resolved') tabMatch = item.status === 'RESOLVED'

        // Priority filter
        const priorityMatch = selectedPriority === 'all' || item.priority?.toLowerCase() === selectedPriority

        return dateMatch && tabMatch && priorityMatch
      })
    : []

  // Pagination
  const totalItems = filteredFeedback.length
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentItems = filteredFeedback.slice(startIndex, endIndex)

  const handleTabChange = tabId => {
    setActiveTab(tabId)
    setCurrentPage(1)
  }

  const handlePriorityChange = e => {
    setSelectedPriority(e.target.value)
    setCurrentPage(1)
  }

  const handleTimeChange = e => {
    setSelectedTime(e.target.value)
    setCurrentPage(1)
  }

  // Handle filter change from MiniHeader
  const handleMiniHeaderFilterChange = (filtered, dateRange) => {
    if (!dateRange) {
      // No date filter applied - reset to show all data
      setIsFiltering(false)
      setDateFilter(null)
      setCurrentPage(1)
      return
    }

    // Date filter is applied - set filtering state
    setIsFiltering(true)
    setDateFilter(dateRange)
    setSelectedTime('all') // Reset existing time filter when using MiniHeader
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleRefresh = () => {
    setCurrentPage(1)
    setSelectedPriority('all')
    setSelectedTime('all')
    setIsFiltering(false)
    setDateFilter(null)
    dispatch(fetchAllFeedback({ forceRefresh: Date.now() }))
  }

  // Helper for status color
  const getStatusDot = status => {
    switch (status) {
      case 'PENDING':
        return <span className="inline-block w-3 h-3 rounded-full bg-yellow-400 mr-2" />
      case 'ON_GOING':
        return <span className="inline-block w-3 h-3 rounded-full bg-blue-400 mr-2" />
      case 'RESOLVED':
        return <span className="inline-block w-3 h-3 rounded-full bg-green-400 mr-2" />
      case 'POST_PONED':
        return <span className="inline-block w-3 h-3 rounded-full bg-gray-400 mr-2" />
      default:
        return <span className="inline-block w-3 h-3 rounded-full bg-gray-300 mr-2" />
    }
  }

  // Modal submit handler
  const handleCreateTicket = async e => {
    e.preventDefault()

    const newTicket = {
      message: ticketName || 'No message',
      priority: selectedPriority,
      status: selectedStatus.toUpperCase(), // Match expected enum like 'PENDING', 'RESOLVED'
      feedbackType: selectedCategory.toUpperCase(), // Example: 'BUG', 'SUGGESTION'
      source: selectedSource,
      createdAt: new Date().toISOString(),
      userId: {
        full_name: ticketOwner || 'Unknown User',
        email: '', // You may fetch or input this
      },
    }

    try {
      await dispatch(createFeedback(newTicket)).unwrap()
      dispatch(fetchAllFeedback()) // Refresh list after creation
      setOpenTicket(false)
      setTicketName('')
      setSelectedStatus('new')
      setSelectedPriority('medium')
      setSelectedCategory('bug')
      setTicketOwner('Anselm Raj')
      setSelectedSource('inApp')
    } catch (err) {
      console.error('Failed to create ticket:', err)
      alert('Error creating ticket: ' + err)
    }
  }

  // Handler for opening ticket details modal
  const handleOpenTicket = ticket => {
    setSelectedTicket(ticket)
  }

  // Handler for closing ticket details modal
  const handleCloseTicket = () => {
    setSelectedTicket(null)
    setShowOptionsMenu(false)
    setShowPropertiesModal(false)
    setShowForwardModal(false)
    setShowChangeStatusModal(false)
  }

  // Handler for opening options menu
  const handleOptionsClick = e => {
    e.stopPropagation()
    setShowOptionsMenu(!showOptionsMenu)
  }

  // Handler for option selection
  const handleOptionSelect = option => {
    setShowOptionsMenu(false)

    switch (option) {
      case 'Reply':
        setTimeout(() => {
          if (replyTextareaRef.current) {
            replyTextareaRef.current.focus()
          }
        }, 100)
        break
      case 'View properties':
        setShowPropertiesModal(true)
        break
      case 'Forward':
        setShowForwardModal(true)
        break
      case 'Change status':
        setShowChangeStatusModal(true)
        break
      case 'Mark as unread':
        handleMarkAsUnread()
        break
      default:
        // No-op
        break
    }
  }

  // Handler for status change
  const handleStatusChange = async status => {
    setSelectedStatus(status)
    if (selectedTicket) {
      try {
        await dispatch(
          updateFeedbackStatus({
            feedbackId: selectedTicket._id,
            status: status.toUpperCase(),
          })
        ).unwrap()
        dispatch(fetchAllFeedback())
        setShowChangeStatusModal(false)
      } catch (err) {
        alert('Failed to update status: ' + err)
      }
    }
  }

  // Handler for forwarding feedback
  const handleForwardFeedback = async () => {
    if (selectedTicket && selectedRespondent) {
      try {
        await dispatch(
          forwardFeedback({
            feedbackId: selectedTicket._id,
            forwardData: {
              recipientEmail: selectedRespondent,
              message: `Forwarding feedback: ${selectedTicket.message || selectedTicket.description}`,
            },
          })
        ).unwrap()
        setShowForwardModal(false)
        setSelectedRespondent('')
        setRespondentSearch('')
        alert('Feedback forwarded successfully')
      } catch (err) {
        alert('Failed to forward feedback: ' + err)
      }
    }
  }

  // Handler for replying to feedback
  const handleReplyToFeedback = async (replyData) => {
    if (selectedTicket && replyData.subject && replyData.message) {
      try {
        await dispatch(
          replyToFeedback({
            feedbackId: selectedTicket._id,
            replyData: {
              subject: replyData.subject,
              message: replyData.message,
              recipientEmail: selectedTicket.userId?.email,
            },
          })
        ).unwrap()
        alert('Reply sent successfully')
        // Refresh feedback list
        dispatch(fetchAllFeedback())
      } catch (err) {
        alert('Failed to send reply: ' + err)
      }
    }
  }

  // Handler for marking feedback as unread
  const handleMarkAsUnread = async () => {
    if (selectedTicket) {
      try {
        await dispatch(markFeedbackUnread(selectedTicket._id)).unwrap()
        dispatch(fetchAllFeedback())
        alert('Feedback marked as unread')
      } catch (err) {
        alert('Failed to mark as unread: ' + err)
      }
    }
  }

  // Helper for closing all submodals
  const closeAllSubmodals = () => {
    setShowStatusSelect(false)
    setShowPrioritySelect(false)
    setShowCategorySelect(false)
    setShowSourceSelect(false)
  }

  return (
    <>
      <MiniHeader
        title="Feedback"
        data={feedback}
        onFilterChange={handleMiniHeaderFilterChange}
      />
      <div className="ml-[250px] p-4 max-w-7xl">
        {/* Top controls */}
        <div className="flex gap-4 items-center my-4">
          <div className="flex">
            <Button onClick={() => setOpenTicket(false)} className="bg-teal-500 text-white px-6 py-2 rounded-lg">
              Generate ticket
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Select
              options={priorityOptions}
              value={selectedPriority}
              icon={<IoIosArrowDown />}
              onChange={handlePriorityChange}
              className="border border-teal-500 rounded-lg"
            />
            <Select
              options={timeOptions}
              value={isFiltering ? 'all' : selectedTime}
              icon={<IoIosArrowDown />}
              onChange={handleTimeChange}
              className={`border rounded-lg ${isFiltering ? 'border-gray-300 bg-gray-100 opacity-50' : 'border-teal-500'}`}
              disabled={isFiltering}
            />
            {isFiltering && (
              <span className="text-sm text-gray-500 ml-2">
                Date filtering active via filter buttons
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <DarkTabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} justify="start" />

        {/* Feedback Cards */}
        <div className="space-y-6">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-400">{error}</div>
          ) : currentItems.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No tickets found.</div>
          ) : (
            currentItems.map(item => (
              <Card key={item._id} className="p-0 border border-gray-200 rounded-xl shadow-none">
                <div className="flex flex-col md:flex-row md:items-center justify-between px-6 py-4">
                  <div className="flex items-center gap-2">
                    {getStatusDot(item.status)}
                    <Typography variant="body" className="font-semibold">
                      Ticket #{item._id?.slice(-5)}
                    </Typography>
                    {item.priority && (
                      <span
                        className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                          item.priority.toLowerCase() === 'high'
                            ? 'bg-red-100 text-red-800'
                            : item.priority.toLowerCase() === 'medium'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {item.priority}
                      </span>
                    )}
                  </div>
                  <Typography className="text-xs text-gray-500 mt-2 md:mt-0">
                    Posted at{' '}
                    {item.createdAt
                      ? new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : ''}
                  </Typography>
                </div>
                <div className="px-6 pb-2">
                  <Typography variant="h6" className="mb-1">
                    {item.feedbackType?.replace(/_/g, ' ') || 'Feedback'}
                  </Typography>
                  <Typography className="text-gray-500 text-sm mb-2">
                    {item.message || item.description || 'No message provided.'}
                  </Typography>
                  {item.adminResponse && (
                    <div className="mt-2 bg-gray-50 p-2 rounded">
                      <Typography className="text-xs text-gray-600">
                        <span className="font-semibold">Admin Response:</span> {item.adminResponse}
                      </Typography>
                    </div>
                  )}
                  {item.agentResponse && (
                    <div className="mt-2 bg-gray-50 p-2 rounded">
                      <Typography className="text-xs text-gray-600">
                        <span className="font-semibold">Agent Response:</span> {item.agentResponse}
                      </Typography>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between px-6 pb-4">
                  <div className="flex items-center gap-2">
                    <img
                      src={item.userId?.image || '/assets/images/house.jpg'}
                      alt="avatar"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <Typography className="font-medium">
                      {item.userId?.full_name || item.userId?.email || 'User'}
                    </Typography>
                  </div>
                  <Button
                    variant="secondary"
                    className="text-teal-600 border-none text-underline bg-transparent font-semibold hover:text-teal-600 hover:border hover:border-teal-600 hover:bg-transparent"
                    onClick={() => handleOpenTicket(item)}
                  >
                    Open Ticket
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-end mt-8">
          <Pagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onRefresh={handleRefresh}
            justify="end"
          />
        </div>

        {/* Generate Ticket Modal */}
        {openTicket && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => {
              setOpenTicket(false)
              closeAllSubmodals()
            }}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4"
              onClick={e => {
                e.stopPropagation()
                closeAllSubmodals()
              }}
              style={{ minWidth: 350 }}
            >
              <div className="bg-teal-500 rounded-t-lg px-4 py-3 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white">Generate ticket</h3>
                <button
                  className="text-white text-2xl"
                  onClick={() => {
                    setOpenTicket(false)
                    closeAllSubmodals()
                  }}
                >
                  <IoIosClose />
                </button>
              </div>
              <form
                onSubmit={handleCreateTicket}
                className="p-6 pt-4"
                autoComplete="off"
                onClick={e => e.stopPropagation()}
              >
                <div className="space-y-4">
                  {/* Ticket Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Name</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500"
                      placeholder="Create a ticket from conversation"
                      value={ticketName}
                      onChange={e => setTicketName(e.target.value)}
                      required
                    />
                  </div>

                  {/* Ticket Status */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Status</label>
                    <div
                      className="w-full border border-gray-300 rounded px-3 py-2 flex items-center justify-between cursor-pointer bg-white"
                      onClick={e => {
                        e.stopPropagation()
                        setShowStatusSelect(true)
                      }}
                    >
                      <span>
                        {statusOptions.find(opt => opt.value === selectedStatus)?.label || 'Select status'}
                      </span>
                      <IoIosArrowDown />
                    </div>
                    {showStatusSelect && (
                      <div
                        className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50"
                        onClick={e => e.stopPropagation()}
                      >
                        {statusOptions.map(option => (
                          <div
                            key={option.value}
                            className={`px-4 py-2 cursor-pointer hover:bg-teal-50 ${
                              selectedStatus === option.value ? 'bg-teal-100 font-semibold' : ''
                            }`}
                            onClick={() => {
                              setSelectedStatus(option.value)
                              setShowStatusSelect(false)
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Priority */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <div
                      className="w-full border border-gray-300 rounded px-3 py-2 flex items-center justify-between cursor-pointer bg-white"
                      onClick={e => {
                        e.stopPropagation()
                        setShowPrioritySelect(true)
                      }}
                    >
                      <span>
                        {priorityOptions.find(opt => opt.value === selectedPriority)?.label || 'Select priority'}
                      </span>
                      <IoIosArrowDown />
                    </div>
                    {showPrioritySelect && (
                      <div
                        className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50"
                        onClick={e => e.stopPropagation()}
                      >
                        {priorityOptions
                          .filter(opt => opt.value !== 'all')
                          .map(option => (
                            <div
                              key={option.value}
                              className={`px-4 py-2 cursor-pointer hover:bg-teal-50 ${
                                selectedPriority === option.value ? 'bg-teal-100 font-semibold' : ''
                              }`}
                              onClick={() => {
                                setSelectedPriority(option.value)
                                setShowPrioritySelect(false)
                              }}
                            >
                              {option.label}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Category */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <div
                      className="w-full border border-gray-300 rounded px-3 py-2 flex items-center justify-between cursor-pointer bg-white"
                      onClick={e => {
                        e.stopPropagation()
                        setShowCategorySelect(true)
                      }}
                    >
                      <span>
                        {categoryOptions.find(opt => opt.value === selectedCategory)?.label || 'Select category'}
                      </span>
                      <IoIosArrowDown />
                    </div>
                    {showCategorySelect && (
                      <div
                        className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50"
                        onClick={e => e.stopPropagation()}
                      >
                        {categoryOptions.map(option => (
                          <div
                            key={option.value}
                            className={`px-4 py-2 cursor-pointer hover:bg-teal-50 ${
                              selectedCategory === option.value ? 'bg-teal-100 font-semibold' : ''
                            }`}
                            onClick={() => {
                              setSelectedCategory(option.value)
                              setShowCategorySelect(false)
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Ticket Owner */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Owner</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500"
                      placeholder="Anselm Raj"
                      value={ticketOwner}
                      onChange={e => setTicketOwner(e.target.value)}
                      required
                    />
                  </div>

                  {/* Source */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                    <div
                      className="w-full border border-gray-300 rounded px-3 py-2 flex items-center justify-between cursor-pointer bg-white"
                      onClick={e => {
                        e.stopPropagation()
                        setShowSourceSelect(true)
                      }}
                    >
                      <span>
                        {sourceOptions.find(opt => opt.value === selectedSource)?.label || 'Select source'}
                      </span>
                      <IoIosArrowDown />
                    </div>
                    {showSourceSelect && (
                      <div
                        className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50"
                        onClick={e => e.stopPropagation()}
                      >
                        {sourceOptions.map(option => (
                          <div
                            key={option.value}
                            className={`px-4 py-2 cursor-pointer hover:bg-teal-50 ${
                              selectedSource === option.value ? 'bg-teal-100 font-semibold' : ''
                            }`}
                            onClick={() => {
                              setSelectedSource(option.value)
                              setShowSourceSelect(false)
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Create Button */}
                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2 rounded-md font-medium"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Ticket Details Modal */}
        {selectedTicket && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={handleCloseTicket}
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4" onClick={e => e.stopPropagation()}>
              <div className="p-6">
                {/* Header with three dots menu */}
                <div className="flex justify-between items-center mb-4 relative">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Ticket generated for {selectedTicket.userId?.full_name || selectedTicket.userId?.email || 'User'}:{' '}
                    <span className="text-teal-600 font-bold cursor-pointer">#{selectedTicket._id?.slice(-5)}</span>
                  </h3>
                  <button
                    ref={optionsButtonRef}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-full relative"
                    onClick={handleOptionsClick}
                  >
                    <IoMdMore className="text-2xl" />

                    {/* Options Menu */}
                    {showOptionsMenu && (
                      <div
                        ref={optionsMenuRef}
                        className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 w-48"
                      >
                        <ul className="py-1">
                          <li
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                            onClick={() => handleOptionSelect('Reply')}
                          >
                            <IoIosMail className="text-gray-600" /> Reply
                          </li>
                          <li
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                            onClick={() => handleOptionSelect('View properties')}
                          >
                            <IoIosAlbums className="text-gray-600" /> View properties
                          </li>
                          <li
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                            onClick={() => handleOptionSelect('Forward')}
                          >
                            <BsDownload className="text-gray-600" /> Forward
                          </li>
                          <li
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleOptionSelect('Change status')}
                          >
                            Change status
                          </li>
                          <li
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleOptionSelect('Mark as unread')}
                          >
                            Mark as unread
                          </li>
                        </ul>
                      </div>
                    )}
                  </button>
                </div>

                {/* Ticket Info */}
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      {selectedTicket.createdAt
                        ? new Date(selectedTicket.createdAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 text-sm font-medium bg-gray-100 px-2 py-1 rounded">
                      {selectedTicket.feedbackType?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <img
                      src={selectedTicket.userId?.image || '/assets/images/house.jpg'}
                      alt="avatar"
                      className="w-7 h-7 rounded-full object-cover"
                    />
                    <span className="font-medium text-sm text-gray-800">
                      {selectedTicket.userId?.full_name || selectedTicket.userId?.email || 'User'}
                    </span>
                  </div>
                </div>

                {/* Message */}
                <div className="mt-4 mb-4  pb-4">
                  <p className="text-sm text-gray-700 mb-1">Hi,</p>
                  <p className="text-sm text-gray-700 whitespace-pre-line">
                    {selectedTicket.message || selectedTicket.description || 'No message provided.'}
                  </p>
                  <p className="text-sm text-gray-700 mt-2">Thanks.</p>
                </div>

                {/* Translate/Response Row */}
                <div className="flex justify-between items-center  pb-2 mb-4 mt-2">
                  <span className="text-teal-500 text-xs cursor-pointer">Translate</span>
                </div>

                <div className="flex justify-center items-center pb-2 mb-4 mt-2">
                  <span className="text-gray-400 text-sm">
                    ------------------------------------------------------------------- Response
                    -------------------------------------------------------------------
                  </span>
                </div>

                {/* New Message Box */}
                <div className=" rounded-lg border border-teal-100">
                  <div className="bg-teal-500 rounded-t-lg px-4 py-2 text-white font-semibold">
                    <span>New Message</span>
                  </div>
                  <div className="p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-gray-700 text-xs font-semibold">To:</span>
                      <span className="text-teal-600 text-xs font-medium">
                        {selectedTicket.userId?.full_name || selectedTicket.userId?.email || 'User'}
                      </span>
                    </div>
                    <div className="mb-3">
                      <span className="text-gray-700 text-xs font-semibold">Subject:</span>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 mt-1 focus:outline-none focus:border-teal-500 text-xs"
                        placeholder="Enter subject"
                        value={replySubject}
                        onChange={e => setReplySubject(e.target.value)}
                      />
                    </div>
                    <textarea
                      ref={replyTextareaRef}
                      rows={4}
                      className="w-full border border-gray-300 rounded p-3 mt-2 focus:outline-none focus:border-teal-500 text-sm"
                      placeholder="Type your message here..."
                      value={replyMessage}
                      onChange={e => setReplyMessage(e.target.value)}
                    />
                    <div className="flex justify-end mt-4">
                      <button
                        className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium"
                        onClick={() => {
                          handleReplyToFeedback({
                            subject: replySubject,
                            message: replyMessage
                          })
                          setReplySubject('')
                          setReplyMessage('')
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Compose
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Properties Modal */}
        {showPropertiesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-80 mx-4" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Properties</h3>
                <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowPropertiesModal(false)}>
                  <IoIosClose className="text-2xl" />
                </button>
              </div>
              <div className="p-4">
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Ticket Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">Status:</span>
                      <span className="text-gray-600">{selectedTicket?.status || 'Open'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Agent:</span>
                      <span className="text-gray-600">{selectedTicket?.agentId?.full_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Created date:</span>
                      <span className="text-gray-600">
                        {selectedTicket?.createdAt ? new Date(selectedTicket.createdAt).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Updated:</span>
                      <span className="text-gray-600">
                        {selectedTicket?.updatedAt
                          ? new Date(selectedTicket.updatedAt).toLocaleDateString()
                          : 'Just now'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Session:</span>
                      <span className="text-gray-600">00:22</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Average response time:</span>
                      <span className="text-gray-600">02:22</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Customer Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">Email:</span>
                      <span className="text-gray-600">{selectedTicket?.userId?.email || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Section:</span>
                      <span className="text-gray-600">Realtor/Broker</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Tickets:</span>
                      <span className="text-gray-600">23</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Forward Modal */}
        {showForwardModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-80 mx-4" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b bg-teal-500 border-gray-200 flex justify-between items-center rounded-t-2xl">
                <button className="text-white hover:text-gray-200" onClick={() => setShowForwardModal(false)}>
                  <IoIosClose className="text-2xl" />
                </button>
                <h3 className="text-lg font-semibold text-white">Forward</h3>
                <button
                  className="bg-white hover:bg-gray-100 text-teal-500 px-4 py-2 rounded-full text-sm font-medium"
                  onClick={handleForwardFeedback}
                >
                  Done
                </button>
              </div>
              <div className="p-4">
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Choose respondent</h4>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:border-teal-500 text-sm"
                    placeholder="Type support name or email"
                    value={respondentSearch}
                    onChange={e => {
                      setRespondentSearch(e.target.value)
                      setSelectedRespondent(e.target.value)
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Change Status Modal */}
        {showChangeStatusModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-80 mx-4" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b bg-teal-500 border-gray-200 flex justify-between items-center rounded-t-2xl">
                <button className="text-white hover:text-gray-200" onClick={() => setShowChangeStatusModal(false)}>
                  <IoIosClose className="text-2xl" />
                </button>
                <h3 className="text-lg font-semibold text-white">Change status</h3>
                <button
                  className="bg-white hover:bg-gray-100 text-teal-500 px-4 py-2 rounded-full text-sm font-medium"
                  onClick={() => setShowChangeStatusModal(false)}
                >
                  Done
                </button>
              </div>
              <div className="p-4">
                <div className="space-y-2 mb-4">
                  {statusOptions.map(option => (
                    <div
                      key={option.value}
                      className={`flex items-center justify-between p-3 rounded-full cursor-pointer ${selectedStatus === option.value ? 'bg-teal-50' : 'hover:bg-gray-100'}`}
                      onClick={() => handleStatusChange(option.value)}
                    >
                      <span className={selectedStatus === option.value ? 'font-medium text-teal-700' : ''}>
                        {option.label}
                      </span>
                      {selectedStatus === option.value && <IoIosCheckmark className="text-teal-600" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default Feedback